import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Language } from '@/i18n/types';
import { announceLanguage } from '@/services/announceLanguage';
import { setApiLanguage } from '@/services/api';

// The chosen language (RO default, matching the backend's SessionLocaleResolver default).
//
// Kept client-side and persisted across visits: React never round-trips through the server for a
// render, so a `?lang=` navigation would be a page load for nothing.
//
// It is NOT independent of the server's mechanism any more, which it was while only the cabinet
// was React. Administered content — news, pages, policies — is translated per item, and only the
// server knows which translation exists, so public reads carry `lang` for it to resolve against
// (services/api.ts, setApiLanguage). Without that the server keeps answering in Romanian and
// keeps reporting languageFallback=false, and the "not available in English" notice never shows.
//
// The session request carries it too, on every load, and a switch is announced to the server once
// (announceLanguage), so the refusals the server writes in the session's language follow the
// reader's choice in the cabinet as well.
const STORAGE_KEY = 'akis.cabinet.lang';

function readStoredLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'en' ? 'en' : 'ro';
  } catch {
    return 'ro';
  }
}

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageState>({
  language: 'ro',
  setLanguage: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  // Set synchronously during render, not in the effect below: a page's own load effect runs
  // before this component's, so a request fired on the first render would otherwise go out
  // without the language and come back in the wrong one.
  setApiLanguage(language);

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Best-effort persistence — a private-browsing quota error shouldn't break the toggle.
    }
  }, [language]);

  // The interface changes at once; the server is told alongside, not waited for. Only on a real
  // switch: the first render's language already goes out with the session request.
  const setLanguage = (lang: Language) => {
    if (lang === language) return;
    setLanguageState(lang);
    announceLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
